import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Avatar, 
  Rating, 
  Grid, 
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import axios from 'axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const TestimonialList = ({ propertyId = null, limit = null, showForm = false }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = '/api/testimonials';
      
      if (propertyId) {
        url = `/api/testimonials/property/${propertyId}`;
      }
      
      const { data } = await axios.get(url);
      
      // Apply limit if specified
      const limitedData = limit ? data.slice(0, limit) : data;
      
      setTestimonials(limitedData);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      setError('Не удалось загрузить отзывы. Пожалуйста, попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, [propertyId]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'd MMMM yyyy', { locale: ru });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (testimonials.length === 0) {
    return (
      <Box sx={{ my: 2 }}>
        <Typography variant="body1" color="text.secondary" align="center">
          Отзывов пока нет. Будьте первым, кто оставит отзыв!
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ my: 2 }}>
      <Grid container spacing={3}>
        {testimonials.map((testimonial) => (
          <Grid item xs={12} md={6} key={testimonial.id || testimonial._id}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar 
                    src={testimonial.user?.avatar} 
                    alt={testimonial.user?.name || 'Пользователь'}
                    sx={{ mr: 2 }}
                  >
                    {(testimonial.user?.name || 'U')[0].toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" component="div">
                      {testimonial.user?.name || 'Пользователь'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(testimonial.date)}
                    </Typography>
                  </Box>
                </Box>
                
                <Rating 
                  value={testimonial.rating} 
                  readOnly 
                  precision={0.5}
                  size="small"
                  sx={{ mb: 1 }}
                />
                
                {testimonial.property && (
                  <Typography variant="body2" color="primary" gutterBottom>
                    Объект: {testimonial.property.name}
                  </Typography>
                )}
                
                <Typography variant="body1" component="p" sx={{ mt: 1 }}>
                  {testimonial.text}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default TestimonialList;
